import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'
import { Tonality } from '@dsny/dsny-component-library'
import { Close } from '@dsny/dsny-component-library/icons'
import useArchiveToFilter from 'utils/hooks/useArchiveToFilter'
import {
  ARCHIVED_FILTER_TYPE,
  ARCHIVED_FILTER_ID,
  hasArchivedFilter
} from 'utils/archiveFilter'
import ArchivedFilter from 'components/Filtering/ArchivedFilter/ArchivedFilter'
import SearchableFilterSection from '../SearchableFilterSection'
import StandardFilterSection from '../StandardFilterSection/StandardFilterSection'
import { useFilters } from '../hooks/useFilters'
import {
  getStandardFilterSections,
  getConditionalFilterSections
} from '../filterSectionsConfig'
import {
  MotionPanel,
  PanelHeader,
  PanelContent,
  ScrollableContent,
  FilterSection,
  PanelFooter,
  PanelHeaderInnerContainer
} from './MobileFiltersPanel.styles'

const MobileFiltersPanel = ({ isOpen, onClose }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { enabled: archiveToFilterEnabled } = useArchiveToFilter()

  // Use custom hook for all filter logic
  const {
    genres,
    moods,
    instruments,
    release_types,
    packages,
    versions,
    markets,
    localSelectedFilters,
    hasFilterChanges,
    sortFilterOptions,
    handleFilterToggle,
    handleSingleFilterToggle,
    clearAllFilters,
    applyFilters,
    resetLocalFilters
  } = useFilters({ deferClear: archiveToFilterEnabled })

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Get filter sections configurations
  const standardFilterSections = getStandardFilterSections(
    moods,
    instruments,
    release_types
  )
  const conditionalFilterSections = getConditionalFilterSections(
    packages,
    versions,
    markets,
    theme,
    t
  )

  // Handle closing the panel - reset local state if there are unsaved changes
  const handleClose = () => {
    if (hasFilterChanges) {
      resetLocalFilters()
    }
    onClose()
  }
  const handleArchivedChange = handleFilterToggle(ARCHIVED_FILTER_TYPE, [
    {
      id: ARCHIVED_FILTER_ID,
      name: t('home.archived-releases')
    }
  ])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="mobile-filters-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={handleClose}
          />

          <MotionPanel
            key="mobile-filters-panel"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            <PanelHeader>
              <PanelHeaderInnerContainer>
                <Close
                  width={24}
                  height={24}
                  color={theme.colors.neutralW80}
                  onClick={handleClose}
                  style={{ cursor: 'pointer' }}
                />
                <Tonality.UIText
                  textStyle="bodyBold"
                  style={{ fontSize: '16px', marginLeft: 'auto' }}
                  color={theme.colors.neutralW90}
                >
                  {t('home.filters-title')}
                </Tonality.UIText>

                <Tonality.Button
                  onClick={clearAllFilters}
                  variant="text"
                  setting="player"
                  mode="dark"
                  style={{ marginLeft: 'auto' }}
                  disabled={localSelectedFilters.length === 0}
                >
                  {t('home.clear-button-text')}
                </Tonality.Button>
              </PanelHeaderInnerContainer>
            </PanelHeader>

            <ScrollableContent>
              <PanelContent>
                {archiveToFilterEnabled && (
                  <FilterSection>
                    <ArchivedFilter
                      compact
                      checked={hasArchivedFilter(localSelectedFilters)}
                      label={t('home.show-archived')}
                      onChange={(event) =>
                        handleArchivedChange(
                          event,
                          t('home.archived-releases'),
                          ARCHIVED_FILTER_ID
                        )
                      }
                    />
                  </FilterSection>
                )}
                {/* Genres - Special searchable section */}
                <FilterSection>
                  <SearchableFilterSection
                    title={t('home.filter-genres-title')}
                    options={genres}
                    localSelectedFilters={localSelectedFilters}
                    handleFilterToggle={handleFilterToggle}
                    filterType="genre"
                  />
                </FilterSection>

                {/* Standard filter sections */}
                {standardFilterSections.map((section) => (
                  <StandardFilterSection
                    key={section.key}
                    title={t(section.titleKey)}
                    options={section.options}
                    filterType={section.key}
                    localSelectedFilters={localSelectedFilters}
                    handleFilterToggle={handleFilterToggle}
                    handleSingleFilterToggle={handleSingleFilterToggle}
                    sortFilterOptions={sortFilterOptions}
                    singleSelect={section.singleSelect}
                    theme={theme}
                  />
                ))}

                {/* Conditional filter sections */}
                {conditionalFilterSections.map(
                  (section) =>
                    section.condition && (
                      <StandardFilterSection
                        key={section.key}
                        title={
                          t(section.titleKey) ||
                          section.titleKey.split('.').pop()
                        }
                        options={section.options}
                        filterType={section.key}
                        localSelectedFilters={localSelectedFilters}
                        handleFilterToggle={handleFilterToggle}
                        handleSingleFilterToggle={handleSingleFilterToggle}
                        sortFilterOptions={sortFilterOptions}
                        singleSelect={false}
                        theme={theme}
                        icon={section.icon}
                      >
                        {section.children}
                      </StandardFilterSection>
                    )
                )}
              </PanelContent>
            </ScrollableContent>

            <PanelFooter>
              <Tonality.Button
                onClick={() => {
                  applyFilters()
                  onClose()
                }}
                variant="primary"
                setting="player"
                mode="dark"
                style={{ width: '100%' }}
                disabled={!hasFilterChanges}
              >
                {t('home.apply-filters')}
              </Tonality.Button>
            </PanelFooter>
          </MotionPanel>
        </>
      )}
    </AnimatePresence>
  )
}

export default MobileFiltersPanel
